import React, { useEffect, useState } from 'react';
import styles from './navbar.module.css';
import ChangeLanguage from '../changeLanguage/changeLanguage';
import Login from '../../assets/telephone.svg';
import Hamburger from '../../assets/hamburgerMenu.svg';
import HamburgerScrolled from '../../assets/hamburgerScrolled.svg';
import Login1 from '../../assets/telephoneBlue1.svg';
import CustomsLogo from '../../assets/customsLogo.svg';
import CustomsLogoColored from '../../assets/logoColored.svg';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-scroll";
import { X, Home, Info, Briefcase, Package, Phone, Layers, HelpCircle, Headphones } from "lucide-react";

function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolling, setScrolling] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const { t } = useTranslation();
  const [isOnIdPage, setIsOnIdPage] = useState(false);

  // Dynamic base URL detection — works for any subfolder (/new2, /test, etc.)
  const getBaseUrl = () => {
    const { hostname, pathname, port, protocol } = window.location;

    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return `${protocol}//${hostname}${port ? ':' + port : ''}`;
    }

    // Known app routes — if path starts with these, there's no subfolder prefix
    const knownRoutes = ['career', 'customer-service', 'amount', 'successurl', 'errorurl', 'cancelurl', 'success', 'error', 'cancel', 'callback'];
    const segments = pathname.split('/').filter(Boolean);

    // If first segment is NOT a known route, treat it as a subfolder (e.g. /new2, /test)
    if (segments.length > 0 && !knownRoutes.includes(segments[0].toLowerCase())) {
      return `${protocol}//${hostname}/${segments[0]}`;
    }

    return `${protocol}//${hostname}`;
  };

  const BASE_URL = getBaseUrl();

  const toggleSidebar = () => {
    setIsOpen(!isOpen);
    // Prevent body scroll when sidebar is open
    if (!isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setScrolling(true);
      } else {
        setScrolling(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    const path = window.location.pathname.toLowerCase();
    const isSubPage = path.includes("/amount/") || path.includes("/career") || path.includes("/customer-service");
    setIsOnIdPage(isSubPage);
  }, []);

  // Navigation items with icons for mobile
  const navItems = [
    { id: 'home', label: t("navHome"), icon: <Home size={20} /> },
    { id: 'contact', label: t("navContact"), icon: <Phone size={20} /> },
    { id: 'services', label: 'SERVICES', icon: <Briefcase size={20} /> },
    { id: 'NCTS', label: 'NCTS', icon: <Briefcase size={20} /> },
    { id: 'about', label: t("navAbout"), icon: <Info size={20} /> },
    { id: 'faq', label: t("navFaq"), icon: <HelpCircle size={20} /> },
    { id: 'offers', label: t("navOffers"), icon: <Layers size={20} /> },
  ];

  const handleLogoClick = () => {
    // Redirect to the home page if the URL contains `id` (for example, "/amount/2")
    if (isOnIdPage) {
      window.location.href = BASE_URL;
    } else {
      // If already on home page, scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleNavItemClick = (itemId) => {
    // Only handle clicks when on /amount/ pages
    if (isOnIdPage) {
      if (itemId === 'home') {
        window.location.href = BASE_URL;
      } else {
        window.location.href = `${BASE_URL}#${itemId}`;
      }
    }
    // If on home page, let react-scroll handle it (no custom click handler needed)
  };

  return (
    <div>
      {isMobile ? (
        // Mobile Navigation
        <div className={`${styles.mobileNav} ${scrolling ? styles.navbarScrolled : ""}`}>
          <img 
            src={scrolling ? HamburgerScrolled : Hamburger} 
            className={styles.hamburger} 
            onClick={toggleSidebar}
            alt="Menu"
          />
          
          <img 
            src={scrolling ? CustomsLogoColored : CustomsLogo} 
            className={styles.logo}
            alt="Logo"
            onClick={handleLogoClick}
            style={{ cursor: 'pointer' }}
          />
          
          <div className={styles.mobileNavItems}>
            <ChangeLanguage />
            <a className={styles.dial} href='tel:+995500703003' aria-label="Call us">
              <img 
                src={scrolling ? Login1 : Login} 
                className={styles.login}
                alt="Phone"
              />
            </a>
          </div>
        </div>
      ) : (
        // Desktop Navigation
        <nav className={`${styles.navbar} ${scrolling ? styles.navbarScrolled : ""}`}>
          <img 
            src={scrolling ? CustomsLogoColored : CustomsLogo} 
            className={styles.logo}
            alt="Logo"
            onClick={handleLogoClick}
            style={{ cursor: 'pointer' }}
          />
          
          <div className={styles.navbarItems}>
            {navItems.map((item) => (
              item.page ? (
                // Page navigation links (career, customer-service)
                <a
                  key={item.id}
                  href={`${BASE_URL}${item.page}`}
                  className={styles.navLink}
                >
                  {item.label}
                </a>
              ) : isOnIdPage ? (
                // Regular anchor links for /amount/ pages
                <a
                  key={item.id}
                  href={item.id === 'home' ? BASE_URL : `${BASE_URL}#${item.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    handleNavItemClick(item.id);
                  }}
                  className={styles.navLink}
                >
                  {item.label}
                </a>
              ) : (
                // React-scroll Links for home page
                <Link
                  key={item.id}
                  to={item.id}
                  smooth={true}
                  duration={800}
                  spy={true}
                  activeClass={styles.active}
                  offset={-70}
                >
                  {item.label}
                </Link>
              )
            ))}
          </div>
          
          <div className={styles.navbarRight}>
            <ChangeLanguage />
            <img 
              src={scrolling ? Login1 : Login} 
              className={styles.login}
              alt="Phone"
            />
            <a href='tel:+995500703003' className={styles.number}>
              +995 500-70-30-03
            </a>
          </div>
        </nav>
      )}

      {/* Mobile Sidebar */}
      {isMobile && (
        <>
          <AnimatePresence>
            {isOpen && (
              <motion.div
                initial={{ x: "-100%" }}
                animate={{ x: "0%" }}
                exit={{ x: "-100%" }}
                transition={{ 
                  type: "spring", 
                  stiffness: 300, 
                  damping: 30,
                  duration: 0.3 
                }}
                className={styles.sidebar}
              >
                <button 
                  onClick={toggleSidebar} 
                  className={styles.closeButton}
                  aria-label="Close menu"
                >
                  <X size={24} />
                </button>

                <ul>
                  {navItems.map((item, index) => (
                    <motion.li
                      key={item.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      {item.page ? (
                        // Page navigation links (career, customer-service)
                        <a href={`${BASE_URL}${item.page}`} onClick={toggleSidebar}>
                          {item.icon}
                          {item.label}
                        </a>
                      ) : isOnIdPage ? (
                        // Regular anchor for /amount/ pages
                        <a
                          href={item.id === 'home' ? BASE_URL : `${BASE_URL}#${item.id}`}
                          onClick={(e) => {
                            e.preventDefault();
                            toggleSidebar();
                            setTimeout(() => {
                              handleNavItemClick(item.id);
                            }, 300);
                          }}
                        >
                          {item.icon}
                          {item.label}
                        </a>
                      ) : (
                        // React-scroll Link for home page
                        <Link
                          to={item.id}
                          smooth={true}
                          duration={800}
                          offset={-60}
                          onClick={toggleSidebar}
                        >
                          {item.icon}
                          {item.label}
                        </Link>
                      )}
                    </motion.li>
                  ))}
                  
                  {/* Phone number in sidebar */}
                  <motion.li
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: navItems.length * 0.1 }}
                    style={{ marginTop: '2rem', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '2rem' }}
                  >
                    <a href='tel:+995500703003' style={{ justifyContent: 'center' }}>
                      <Phone size={20} />
                      +995 500-70-30-03
                    </a>
                  </motion.li>
                </ul>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Overlay */}
          <AnimatePresence>
            {isOpen && (
              <motion.div 
                className={styles.overlay} 
                onClick={toggleSidebar}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              />
            )}
          </AnimatePresence>
        </>
      )}
    </div>
  );
}

export default Navbar;